import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useBMP } from '../context/BMPContext';
import useMarketProfile from '../hooks/useMarketProfile';
import { useTranslation } from 'react-i18next';
import './ProfileView.css';

const API = process.env.REACT_APP_API_URL;

export default function ProfileView() {
  const { t } = useTranslation();
  const { profile, viewParams, refreshProfile, bmpNavigate } = useBMP();
  const { saveProfile, saving } = useMarketProfile();
  const [bannerUploading, setBannerUploading] = useState(false);

  const [form, setForm] = useState({
    role: viewParams?.defaultRole || profile?.role || 'productor',
    company_name: profile?.company_name || '',
    ruc: profile?.ruc || '',
    zone: profile?.zone || 'norte',
    city: profile?.city || '',
    phone: profile?.phone || '',
    whatsapp: profile?.whatsapp || '',
    description: profile?.description || '',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        role: profile.role,
        company_name: profile.company_name || '',
        ruc: profile.ruc || '',
        zone: profile.zone || 'norte',
        city: profile.city || '',
        phone: profile.phone || '',
        whatsapp: profile.whatsapp || '',
        description: profile.description || '',
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await saveProfile(form);
      await refreshProfile();
      bmpNavigate('catalog');
    } catch (err) {
      alert(t('mkt_save_error'));
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await axios.post(`${API}/market/profile/banner/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshProfile();
    } catch (err) {
      alert(t('bmp_upload_error'));
    } finally {
      setBannerUploading(false);
    }
  };

  return (
    <div className="profile-view">
      <h2>{profile ? t('bmp_edit_profile') : t('bmp_create_profile')}</h2>

      {/* Banner upload */}
      {profile && (
        <div className="pv-banner-section bmp-card">
          <label className="pv-banner-label">{t('bmp_store_banner')}</label>
          <div className="pv-banner-preview">
            {profile.banner_url ? (
              <img src={profile.banner_url} alt="Banner" className="pv-banner-img" />
            ) : (
              <div className="pv-banner-placeholder">
                <span>{t('bmp_no_banner')}</span>
              </div>
            )}
            <label className="pv-banner-upload-btn">
              {bannerUploading ? t('bmp_uploading') : t('bmp_change_banner')}
              <input type="file" accept="image/*" onChange={handleBannerUpload} hidden disabled={bannerUploading} />
            </label>
          </div>
          <p className="pv-banner-hint">{t('bmp_banner_hint')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-form bmp-panel">
        <div className="bmp-form-row">
          <div className="bmp-field">
            <label>{t('mkt_role')}</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} disabled={!!profile}>
              <option value="productor">{t('mkt_producer')}</option>
              <option value="exportadora">{t('mkt_exporter')}</option>
              <option value="comercial">{t('mkt_commercial')}</option>
            </select>
          </div>
          <div className="bmp-field">
            <label>{t('mkt_company')}</label>
            <input type="text" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder={t('mkt_company_placeholder')} />
          </div>
          <div className="bmp-field">
            <label>{t('mkt_ruc')}</label>
            <input type="text" value={form.ruc} onChange={e => setForm(f => ({ ...f, ruc: e.target.value }))} placeholder="0901234567001" />
          </div>
        </div>
        <div className="bmp-form-row">
          <div className="bmp-field">
            <label>{t('mkt_zone')}</label>
            <select value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))}>
              <option value="norte">{t('mkt_zone_north')}</option>
              <option value="sur">{t('mkt_zone_south')}</option>
            </select>
          </div>
          <div className="bmp-field">
            <label>{t('mkt_city')}</label>
            <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder={t('mkt_city_placeholder')} />
          </div>
          <div className="bmp-field">
            <label>{t('mkt_phone')}</label>
            <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+593..." />
          </div>
        </div>
        <div className="bmp-form-row">
          <div className="bmp-field">
            <label>WhatsApp</label>
            <input type="tel" value={form.whatsapp} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder="+593..." />
          </div>
        </div>
        <div className="bmp-field">
          <label>{t('mkt_description')}</label>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder={t('mkt_description_placeholder')} maxLength={500} />
        </div>
        <div className="bmp-form-actions">
          {profile && <button type="button" className="bmp-btn bmp-btn-secondary" onClick={() => bmpNavigate('catalog')}>{t('cancel')}</button>}
          <button type="submit" className="bmp-btn bmp-btn-primary" disabled={saving}>{saving ? t('saving') : t('wc_save')}</button>
        </div>
      </form>
    </div>
  );
}
